import { type IconName, Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';

import { getVariationStrokeColor } from '../../utils/getVariationStrokeColor';

import styles from './TooltipIcon.module.css';

/** Get stroke color for a fill - uses the variation palette or falls back to the fill itself
 *
 * NOTE: This may have to change in the future if we want consumers to be able to pass in a custom color as it will NOT work with LP variables.
 * Our default colors in this package are currently using hexes though so that's fine.
 */
const getStrokeColor = (fill: string): string => getVariationStrokeColor(fill) ?? fill;

export interface TooltipIconProps {
  /**
   * The icon name from LaunchPad design system.
   * @default 'diamond'
   */
  name?: IconName;
  /**
   * Color for the icon fill. Stroke color will be automatically computed.
   */
  color: string;
  /**
   * Optional className to apply to the icon.
   */
  className?: string;
}

/**
 * A reusable component for rendering tooltip row icons.
 * Simply provide the icon name and color, and it handles all the styling automatically.
 * Sets size="small" and computes stroke color from the fill color.
 * Supports any icon from the LaunchPad design system.
 *
 * @example
 * ```tsx
 * <TooltipIcon name="circle" color="#ff0000" />
 * <TooltipIcon name="diamond" color="#00ff00" />
 * ```
 */
export function TooltipIcon({ name = 'diamond', color, className }: TooltipIconProps) {
  const strokeColor = getStrokeColor(color);
  const hasStroke = strokeColor !== color;

  return (
    <div
      className={clsx(styles.tooltipIcon, className)}
      style={{
        // @ts-expect-error unknown css property
        '--fill-color': color,
        '--stroke-color': strokeColor,
        '--stroke-width': hasStroke ? 2 : undefined,
      }}
    >
      {hasStroke ? <Icon name={name} size="tiny" className={styles.outline} /> : null}
      <Icon name={name} style={{ fill: color }} size="tiny" className={styles.fill} />
    </div>
  );
}
